import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BaseContentForm from './BaseContentForm';
import { FaUnlink, FaRobot, FaSpinner, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { contentService } from '../services/contentService';
import llmService from '../services/llmService.js';
import '../styles/ViewContentForm.css';

/**
 * ViewContentForm Component
 * Form for viewing and editing existing content entries
 * 
 * @component
 */
function ViewContentForm({ 
  isOpen, 
  onClose, 
  content,
  contentTypes,
  onUpdate,
  onViewHistory,
  savedContents
}) {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [showEnhancementModal, setShowEnhancementModal] = useState(false);
  const [enhancementResult, setEnhancementResult] = useState(null);
  const handleUnshare = async () => {
    if (!content) return;
    
    try {
      await contentService.unshareContent(content.id);
      toast.success('Content unshared successfully');
      // Refresh the content list
      if (onUpdate) {
        onUpdate(content);
      }
    } catch (error) {
      toast.error('Failed to unshare content');
    }
  };

  const handleEnhanceWithAI = async (enhancementType) => {
    if (!content?.id) return;

    setIsEnhancing(true);
    try {
      const result = await llmService.enhanceContent({
        contentId: content.id,
        enhancementType: enhancementType
      });

      if (result.success) {
        setEnhancementResult({
          success: true,
          original: result.data.original_content,
          enhanced: result.data.response_text,
          enhancementType: result.data.enhancement_type,
          costEstimate: result.data.cost_estimate
        });
        setShowEnhancementModal(true);
        
        if (result.data.cost_estimate) {
          toast.info(`Enhancement cost: $${result.data.cost_estimate.toFixed(4)}`, { autoClose: 3000 });
        }
      } else {
        const errorMessage = llmService.formatErrorMessage(result.errorType, result.error);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error enhancing content:', error);
      toast.error('Failed to enhance content. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleAcceptEnhancement = () => {
    if (enhancementResult?.enhanced && content) {
      const updatedContent = {
        ...content,
        content: enhancementResult.enhanced
      };
      onUpdate(updatedContent);
      setShowEnhancementModal(false);
      setEnhancementResult(null);
      toast.success('Content enhanced successfully!');
    }
  };

  const handleRejectEnhancement = () => {
    setShowEnhancementModal(false);
    setEnhancementResult(null);
  };

  const extraActions = (
    <div className="form-actions-left">
      <button type="button" onClick={onViewHistory}>
        View History
      </button>
      
      <div className="enhance-dropdown">
        <button 
          type="button" 
          className="enhance-button"
          disabled={isEnhancing}
        >
          {isEnhancing ? <FaSpinner className="spinner" /> : <FaRobot />}
          Enhance with AI
        </button>
        <div className="enhance-dropdown-content">
          {llmService.getEnhancementTypes().map(enhancement => (
            <button
              key={enhancement.value}
              type="button"
              onClick={() => handleEnhanceWithAI(enhancement.value)}
              disabled={isEnhancing}
              title={enhancement.description}
            >
              {enhancement.label}
            </button>
          ))}
        </div>
      </div>
      
      {content && content.shared_links && content.shared_links.length > 0 && (
        <button 
          className="unshare-button"
          onClick={handleUnshare}
          title="Unshare content"
        >
          <FaUnlink /> Unshare
        </button>
      )}
    </div>
  );

  const handleSubmit = (formData) => {
    if (!content) return;
    
    onUpdate({
      ...formData,
      id: content.id,
      contextReferences: formData.contextReferences || content.context_references || []
    });
  };

  // Format the content to ensure context references are properly displayed
  const formatContentForEditing = (content) => {
    if (!content) return null;
    
    // If the content already has properly formatted context references, return as is
    if (content.content?.includes('~~[@')) {
      return content;
    }
    
    // Convert any old format context references to the new format
    let formattedContent = content.content;
    const oldContextPattern = /(?:^|\s)\[@(\d+)\]/g;
    
    // Add a space at the start if there isn't one and there's a context reference
    if (formattedContent.startsWith('[@')) {
      formattedContent = ' ' + formattedContent;
    }
    
    formattedContent = formattedContent.replace(oldContextPattern, (match, id) => {
      // If the match is at the start of the string, ensure there's a leading space
      const needsLeadingSpace = match.startsWith('[@');
      return `${needsLeadingSpace ? ' ' : ''}~~${match.trim()}~~ `;
    });
    
    // Ensure content_type is properly handled as an ID
    const contentTypeId = typeof content.content_type === 'object' && content.content_type !== null
      ? content.content_type.id
      : (typeof content.content_type === 'number' ? content.content_type : null);
    
    const result = {
      ...content,
      content: formattedContent,
      content_type: contentTypeId,
      contextReferences: content?.context_references || []
    };
    
    return result;
  };

  if (!content) {
    return null;
  }

  return (
    <>
      <BaseContentForm
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleSubmit}
        contentTypes={contentTypes}
        savedContents={savedContents}
        initialContent={formatContentForEditing(content)}
        title="View Content"
        submitButtonText="Update"
        extraActions={extraActions}
      />
      
      {/* Enhancement Comparison Modal */}
      {showEnhancementModal && enhancementResult && (
        <div className="enhancement-modal-overlay" onClick={handleRejectEnhancement}>
          <div className="enhancement-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enhancement-modal-header">
              <h3>AI Enhancement: {llmService.getEnhancementTypes().find(e => e.value === enhancementResult.enhancementType)?.label}</h3>
              <button className="modal-close" onClick={handleRejectEnhancement}>×</button>
            </div>
            
            <div className="enhancement-comparison">
              <div className="comparison-section">
                <h4>Original Content</h4>
                <div className="content-preview original">
                  {enhancementResult.original?.content || content.content}
                </div>
              </div>
              
              <div className="comparison-section">
                <h4>Enhanced Content</h4>
                <div className="content-preview enhanced">
                  {enhancementResult.enhanced}
                </div>
              </div>
            </div>
            
            <div className="enhancement-modal-actions">
              <button 
                className="accept-button"
                onClick={handleAcceptEnhancement}
              >
                <FaCheckCircle /> Accept Enhancement
              </button>
              <button 
                className="reject-button"
                onClick={handleRejectEnhancement}
              >
                <FaTimesCircle /> Keep Original
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

ViewContentForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  content: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    content: PropTypes.string.isRequired,
    content_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    shared_links: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired
    }))
  }),
  contentTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired
  })).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
  savedContents: PropTypes.array
};

export default ViewContentForm; 